import {
  DesignerSkill,
  ICreateRecipePayload,
  Product,
  ProductImage,
  ProjectImageAttribution,
  RecipeModel,
  RecipeTemplate
} from 'src/common/common.interface'
import { PhotosNeeded, RecipeFormulaSize, RecipeFormulaStyle } from 'src/common/enum'
import { RecipeFormula } from 'src/api/recipeFormula'
import { getHttp, putHttp, postHttp } from './apiLayer'
import { handleGetAllData } from './helper'

export interface RecipesPayload {
  unitProductId: number
  eventOrderId: number
}

export enum PRICE_UNIT_TYPE {
  TABLE = 'TABLE',
  GUEST = 'GUEST',
  ITEM = 'ITEM',
  SQ_FT = 'SQ_FT'
}

export enum PRODUCT_IMAGE_TYPE {
  DEFAULT = 'DEFAULT',
  INSPO = 'INSPO',
  RESULT = 'RESULT'
}

export enum PRODUCT_DETAIL_TYPE {
  BOUQ_BR = 'BOUQ_BR',
  BOUQ_BM = 'BOUQ_BM',
  BOUT = 'BOUT',
  PERSONAL = 'PERSONAL',
  CEREMONY = 'CEREMONY',
  RECEPTION = 'RECEPTION',
  COCKTAIL = 'COCKTAIL',
  RENTAL = 'RENTAL',
  ARCH_CLUSTER = 'ARCH_CLUSTER',
  BUD_VASE = 'BUD_VASE',
  CENTERPIECE = 'CENTERPIECE',
  CLOUD = 'CLOUD',
  CORSAGE = 'CORSAGE',
  OTHER = 'OTHER',
  SMALL_BRIDAL_BOUQUET = 'SMALL_BRIDAL_BOUQUET',
  MEDIUM_BRIDAL_BOUQUET = 'MEDIUM_BRIDAL_BOUQUET',
  LARGE_BRIDAL_BOUQUET = 'LARGE_BRIDAL_BOUQUET',
  CASCADING_BOUQUET = 'CASCADING_BOUQUET',
  SMALL_BRIDESMAID_BOUQUET = 'SMALL_BRIDESMAID_BOUQUET',
  MEDIUM_BRIDESMAID_BOUQUET = 'MEDIUM_BRIDESMAID_BOUQUET',
  LARGE_BRIDESMAID_BOUQUET = 'LARGE_BRIDESMAID_BOUQUET',
  POCKET_BOUTONNIERE = 'POCKET_BOUTONNIERE',
  LAPEL_BOUTONNIERE = 'LAPEL_BOUTONNIERE',
  PIN_CORSAGE = 'PIN_CORSAGE',
  CUFF_CORSAGE = 'CUFF_CORSAGE',
  PARTIAL_ARCH_COVERAGE = 'PARTIAL_ARCH_COVERAGE',
  FULL_ARCH_COVERAGE = 'FULL_ARCH_COVERAGE',
  COMPOTE_CENTERPIECE = 'COMPOTE_CENTERPIECE',
  DOG_COLLAR = 'DOG_COLLAR',
  SMALL_FLOWER_CLOUD = 'SMALL_FLOWER_CLOUD',
  MEDIUM_FLOWER_CLOUD = 'MEDIUM_FLOWER_CLOUD',
  LARGE_FLOWER_CLOUD = 'LARGE_FLOWER_CLOUD',
  FLAT_LAY_FLOWERS = 'FLAT_LAY_FLOWERS',
  SMALL_ARCH_CLUSTER = 'SMALL_ARCH_CLUSTER',
  MEDIUM_ARCH_CLUSTER = 'MEDIUM_ARCH_CLUSTER',
  LARGE_ARCH_CLUSTER = 'LARGE_ARCH_CLUSTER',
  DECONSTRUCTED_ARCH_ASYMMETRICAL = 'DECONSTRUCTED_ARCH_ASYMMETRICAL',
  DECONSTRUCTED_ARCH_FULL = 'DECONSTRUCTED_ARCH_FULL',
  CHUPPAH = 'CHUPPAH',
  TABLE_WREATH_RING = 'TABLE_WREATH_RING',
  CHAIR_POSIES = 'CHAIR_POSIES',
  CAKE_FLOWERS_STANDARD = 'CAKE_FLOWERS_STANDARD',
  CAKE_FLOWERS_MEADOW = 'CAKE_FLOWERS_MEADOW',
  LOOSE_STEMS = 'LOOSE_STEMS',
  LOOSE_GREENERY_BY_FT = 'LOOSE_GREENERY_BY_FT',
  '360_CLOUDS' = '360_CLOUDS',
  WELCOME_SIGN = 'WELCOME_SIGN',
  PETITE_CENTERPIECE = 'PETITE_CENTERPIECE',
  BOUT_MULTIPLE = 'BOUT_MULTIPLE',
  PIN_CORSAGE_MULTIPLE = 'PIN_CORSAGE_MULTIPLE',
  SWEETHEART_TABLE = 'SWEETHEART_TABLE',
  IKEBANA_DISH_SMALL = 'IKEBANA_DISH_SMALL',
  IKEBANA_DISH_LARGE = 'IKEBANA_DISH_LARGE',
  FLOWER_CROWN_CHILD = 'FLOWER_CROWN_CHILD',
  FLOWER_CROWN_ADULT = 'FLOWER_CROWN_ADULT',
  PEW_MARKERS = 'PEW_MARKERS',
  HAIR_COMB = 'HAIR_COMB'
}

export enum PRODUCT_DETAIL_SHAPE {
  CLASSIC = 'CLASSIC',
  LUSH_GARDEN = 'LUSH_GARDEN',
  ASYMMETRICAL = 'ASYMMETRICAL',
  CASCADING = 'CASCADING'
}

export enum PRODUCT_DETAIL_SHAPE_V2 {
  CLASSIC = 'CLASSIC',
  LUSH_GARDEN = 'LUSH_GARDEN',
  ASYMMETRICAL = 'ASYMMETRICAL',
  CASCADING = 'CASCADING',
  NO_SHAPE = 'NO_SHAPE'
}

export enum PRODUCT_SEASON {
  SUMMER = 'Summer',
  SPRING = 'Spring',
  FALL = 'Fall',
  WINTER = 'Winter'
}

export interface CreateCustomProductPayload {
  name: string
  productType: PRODUCT_DETAIL_TYPE[]
  price?: number
  priceUnit?: PRICE_UNIT_TYPE
  priceUnitDisplay?: string
  productColor?: string[]
  productShape?: PRODUCT_DETAIL_SHAPE
  productSeason?: PRODUCT_SEASON[]
  productStyle?: RecipeFormulaStyle
  productSize?: RecipeFormulaSize
  productColorComplexity?: string
  imageUrl: string
  productNotes?: string
  publicNotes?: string
  defaultPrepTimeMinutes?: number
  defaultDesignTimeMinutes?: number
  defaultInstallTimeMinutes?: number
  defaultLoadingTimeMinutes?: number
  defaultInstructions?: string
  defaultPhotosNeeded?: PhotosNeeded[]
  attribution?: ProjectImageAttribution
  venueId?: number
  designerSkillIds?: number[]
}

export interface CreateProductPayload {
  name: string
  productType: PRODUCT_DETAIL_TYPE[]
  price?: number
  priceUnit?: PRICE_UNIT_TYPE
  priceUnitDisplay?: string
  productColor?: string[]
  productShape?: PRODUCT_DETAIL_SHAPE
  productSeason?: PRODUCT_SEASON[]
  productStyle?: RecipeFormulaStyle
  productSize?: RecipeFormulaSize
  productColorComplexity?: string
  imageUrl: string
  productNotes?: string
  publicNotes?: string
  defaultPrepTimeMinutes?: number
  defaultDesignTimeMinutes?: number
  defaultInstallTimeMinutes?: number
  defaultLoadingTimeMinutes?: number
  defaultInstructions?: string
  defaultPhotosNeeded?: PhotosNeeded[]
  attribution?: ProjectImageAttribution
  venueId?: number
  designerSkillIds?: number[]
}

export interface UpdateProductPayload extends CreateProductPayload {
  // a case using
  onlyUpdateDefaultImage?: boolean
  // a case using
  onlyUpdateChangeResultImageToInspoImage?: boolean
  // a case using
  onlyUpdateEventToProductImage?: boolean
  eventId?: number
  //
  // a case using
  onlyUpdateInspoImageAsDefaultImage?: boolean
  // a case using
  onlyRemoveInspoImage?: boolean
  // a case using
  onlyUpdateDefaultImageByCropped?: boolean
}

export type UpdateProduct = Omit<UpdateProductPayload, 'name' | 'productType' | 'imageUrl'>

export interface IGetProductPayload {
  q?: string
  cat?: string
  color?: string
  template?: string
  stem?: string
  page?: number
  limit?: number
  isAll?: boolean
  isReview?: boolean
  isReal?: boolean
  isDuplicate?: string
  isDoNotAdd?: string
  recipe?: string
  shape?: string
  venue?: string
  keystoneTag?: string
  max?: number
  min?: number
  style?: string
}

export interface IGetRecipesByProductResponse {
  recipeTemplate: RecipeTemplate
  recipe: RecipeModel[]
  recipeCount: number
}

export interface UpdateProductImagePayload {
  productImages: ProductImage[]
}

export interface ICreateNewCustomProductWithRecipeTemplatePayload {
  productPayload: CreateCustomProductPayload
  recipe?: ICreateRecipePayload
  eventId: number
}

export interface ICreateNewProductWithRecipeTemplatePayload {
  productPayload: CreateProductPayload
  recipe?: ICreateRecipePayload
}

export interface IUpdateProductWithRecipeTemplatePayload {
  productPayload: UpdateProductPayload
  recipe?: ICreateRecipePayload
}

export interface IGetRecipesByProductIdPayload {
  page?: number
  limit?: number
  isAll?: boolean
}

export interface IGetProductForCollectionPayload extends IGetRecipesByProductIdPayload {
  q?: string
}
export interface IGetProductsResponse {
  count: number
  result: Product[]
}

export const getProducts = async (): Promise<Product[]> => {
  const result = await getHttp('/getProducts?limit=1000')
  return result.data?.result || []
}

export const getListProducts = async (payload: IGetProductPayload): Promise<Product[]> => {
  const params = { ...payload }
  params.q = encodeURIComponent(payload.q || '')
  params.isDuplicate = payload.isDuplicate ? 'is-duplicate' : ''
  params.isDoNotAdd = payload.isDoNotAdd ? 'do-not-add' : ''

  const result = await getHttp(`/getProducts`, { params })
  return result.data

  // if (payload.isAll) {
  //   const result = await getHttp(
  //     `/getProducts?q=${encodeURIComponent(payload.q || '')}&cat=${payload.cat || ''}&color=${
  //       payload.color || ''
  //     }&isAll=${true}&isReview=${payload.isReview}`
  //   )
  //   return result.data
  // }
  // const result = await getHttp(
  //   `/getProducts?q=${encodeURIComponent(payload.q || '')}&cat=${payload.cat || ''}&color=${
  //     payload.color || ''
  //   }&keystoneTag=${payload.keystoneTag || ''}&template=${payload.template || ''}&stem=${payload.stem || ''}&isReview=${
  //     payload.isReview
  //   }&isDuplicate=${payload.isDuplicate ? 'is-duplicate' : ''}&isDoNotAdd=${
  //     payload.isDoNotAdd ? 'do-not-add' : ''
  //   }&recipe=${payload.recipe || ''}&shape=${payload.shape || ''}&isAll=${true}`
  // )
  // return result.data
}

export const getEventProducts = async (eventId: number) => {
  const result = await getHttp(`events/${eventId}/products`)
  return result.data
}

export const createCustomsProduct = async (eventId: number, payload: CreateCustomProductPayload): Promise<Product> => {
  const result = await postHttp(`events/${eventId}/products/customs`, payload)
  return result.data
}

export const createProduct = async (payload: CreateProductPayload): Promise<Product> => {
  const result = await postHttp(`products`, payload)
  return result.data
}

export const updateProduct = async (productId: number, payload: UpdateProductPayload): Promise<Product> => {
  const result = await putHttp(`products/${productId}`, payload)
  return result.data
}

export const getDetailProduct = async (productId: number): Promise<Product> => {
  const result = await getHttp(`products/${productId}`)
  return result.data
}

export const getRecipesByProduct = async (
  productId: number,
  payload: IGetRecipesByProductIdPayload
): Promise<IGetRecipesByProductResponse> => {
  const params = { ...payload }
  const result = await getHttp(`products/${productId}/recipes`, { params })
  return result.data
}

export const saveProductToCatalog = async (productId: number): Promise<Product> => {
  const result = await putHttp(`products/${productId}/save-to-catalog`)
  return result.data
}

export const saveProductResultOrInspoImages = async (
  productId: number,
  payload: UpdateProductImagePayload
): Promise<ProductImage[]> => {
  const result = await postHttp(`products/${productId}/result-inspo-images`, payload)
  return result.data
}

export const markAsDuplicateProduct = async (oldProductId: number, newProductId: number): Promise<Product> => {
  const result = await putHttp(`products/${oldProductId}/mark-as-duplicate/${newProductId}`)
  return result.data
}

export const markAsDoNotAddProduct = async (productId: number): Promise<Product> => {
  const result = await putHttp(`products/${productId}/mark-as-do-not-add`)
  return result.data
}

export const removeProductFromCatalog = async (productId: number): Promise<Product> => {
  const result = await putHttp(`products/${productId}/remove-from-catalog`)
  return result.data
}

export const duplicateProduct = async (productId: number): Promise<{ id: number; name: string }> => {
  const result = await postHttp(`products/${productId}/duplicate`)
  return result.data
}

export const createCustomProductWithRecipeTemplate = async (
  eventId: number,
  payload: ICreateNewCustomProductWithRecipeTemplatePayload
): Promise<Product> => {
  const result = await postHttp(`events/${eventId}/products/custom-with-recipe-templates`, payload)
  return result.data
}

export const createProductWithRecipeTemplate = async (
  payload: ICreateNewProductWithRecipeTemplatePayload
): Promise<Product> => {
  const result = await postHttp(`products/with-recipe-templates`, payload)
  return result.data
}

export const updateProductWithRecipeTemplate = async (
  productId: number,
  payload: IUpdateProductWithRecipeTemplatePayload
): Promise<Product & { recipeTemplate: RecipeTemplate }> => {
  const result = await putHttp(`products/${productId}/with-recipe-templates`, payload)
  return result.data
}

export const findBestMatchingFormula = async (productId: number): Promise<RecipeFormula[]> => {
  const result = await getHttp(`find-recipe-formulas/${productId}/best-matching-formula`)
  return result.data
}

export const getAllProductsForCollection = async () => {
  const resultData = await handleGetAllData<Product>('products/get-products-for-collection', 100)

  return resultData as Product[]
}

export const getAllProductsByCollectionId = async (collectionId: number): Promise<Product[]> => {
  const resultData = await getHttp(`products/get-products-for-collection/${collectionId}`)

  return resultData?.data?.result as Product[]
}

export const getAllDesignerSkills = async () => {
  const resultData = await handleGetAllData<DesignerSkill>('designer-skills', 100)

  return resultData as DesignerSkill[]
}
